import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { AppBar } from "../../../components/mobileUi";
import {
  CenterSpinner,
  EmptyText,
  FilterField,
  FilterPanel,
  ListHeaderRow,
  LoadMore,
  M,
  RowKV,
  SelectSheet,
  StatCard,
  SummaryCard,
  useSheet,
} from "../../../components/managementUi";
import {
  DesktopPage,
  DesktopPanel,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../../components/desktopUi";
import { appImages } from "../../../assets/appicons";
import { formatVnd } from "../../../utils/financeFormat";
import { useEmployeeAuth } from "../../../auth/EmployeeAuthContext";
import { JOB_TITLE, filterJobPosition } from "../../../utils/managerRole";
import {
  ALL_OPTION,
  buildOptionSheetData,
  useFilterOptions,
  validId,
} from "../../../utils/useFilterOptions";
import { getManagerUnpaidEmployeeListApi } from "../../../api/incomeManager.api";
import { EMP_BASE_PATH } from "../../../config";

// Khớp app: app/(feature)/management/income/unpaid/list.tsx
const LIMIT = 10;

const SORT_OPTIONS = [
  { key: "DESC", label: "Chưa trả nhiều đến ít", sortOrder: "desc" },
  { key: "ASC", label: "Chưa trả ít đến nhiều", sortOrder: "asc" },
];

const employeeTitle = (item) =>
  `${item?.fullName || ""} ${item?.fullName && item?.code ? "-" : ""} ${item?.code || ""}`.trim();

const EmployeeCard = ({ item, onClick }) => (
  <Box
    as="button"
    type="button"
    w="100%"
    textAlign="left"
    onClick={onClick}
    bg="white"
    border="1px solid"
    borderColor={M.border}
    borderRadius="12px"
    p="14px"
  >
    <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={2}>
      {employeeTitle(item) || "-"}
    </Text>

    <Box h="1px" bg={M.border} mt="12px" mb="12px" />

    <Flex direction="column" gap="8px">
      <RowKV
        label="Chưa được chi trả"
        value={formatVnd(item?.totalUnpaid || 0)}
        valueColor={M.primary}
      />
      <RowKV label="Giao dịch chưa trả hết" value={String(item?.totalTransaction || 0)} />
      <RowKV label="Đã được chi trả" value={formatVnd(item?.totalPaid || 0)} />
      <RowKV
        label="Tổng PDV sau thuế"
        value={formatVnd(item?.totalRemainingRevenueAfterTax || 0)}
      />
      <RowKV label="Khối" value={item?.saleBlock?.name || "-"} />
      <RowKV label="Vùng" value={item?.saleArea?.name || "-"} />
      <RowKV label="Chi nhánh" value={item?.branch?.name || "-"} />
    </Flex>
  </Box>
);

const IncomeUnpaidEmployeeList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { user } = useEmployeeAuth();
  const jobPosition = filterJobPosition(user);

  const [filterOpen, setFilterOpen] = useState(false);
  const [area, setArea] = useState(ALL_OPTION);
  const [block, setBlock] = useState(ALL_OPTION);
  const [branch, setBranch] = useState(ALL_OPTION);
  const [sortKey, setSortKey] = useState("DESC");

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const sheet = useSheet();
  const [sheetSearch, setSheetSearch] = useState("");
  const sheetExtra = useMemo(
    () => (sheet.type === "BLOCK" ? { saleAreaId: validId(area) } : undefined),
    [sheet.type, area]
  );
  const options = useFilterOptions(sheet.type, sheetSearch, sheetExtra);

  const sortOption = SORT_OPTIONS.find((s) => s.key === sortKey) || SORT_OPTIONS[0];

  // Chọn lại vùng thì reset khối (khối phụ thuộc vùng) - khớp app.
  useEffect(() => {
    setBlock(ALL_OPTION);
  }, [area?._id]);

  const buildParams = useCallback(
    (p) => ({
      page: p,
      limit: LIMIT,
      sortOrder: sortOption.sortOrder,
      saleAreaId: validId(area),
      saleBlockId: validId(block),
      branchId: validId(branch),
    }),
    [sortOption, area, block, branch]
  );

  const fetchPage = useCallback(
    async (p, mode) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getManagerUnpaidEmployeeListApi(buildParams(p));
        if (rid !== reqId.current) return;
        const list = data?.items || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setSummary(data?.summary || null);
        setHasNext(!!data?.pagination?.hasNextPage);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") {
          setItems([]);
          setSummary(null);
          setHasNext(false);
        }
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchPage(1, "replace");
  }, [fetchPage]);

  // Bộ lọc hiển thị theo chức vụ - cùng quy ước với màn "Thống kê nhân viên".
  const filterFields = useMemo(() => {
    const f = {
      branch: (
        <FilterField
          key="branch"
          label="Chi nhánh"
          value={branch?.name || "Tất cả"}
          onClick={() => sheet.open("BRANCH")}
        />
      ),
      area: (
        <FilterField
          key="area"
          label="Vùng"
          value={area?.name || "Tất cả"}
          onClick={() => sheet.open("AREA")}
        />
      ),
      block: (
        <FilterField
          key="block"
          label="Khối"
          value={block?.name || "Tất cả"}
          onClick={() => sheet.open("BLOCK")}
        />
      ),
    };
    switch (jobPosition) {
      case JOB_TITLE.TGD:
        return [f.branch, f.area, f.block];
      case JOB_TITLE.TRUONG_VUNG:
      case JOB_TITLE.TRUONG_KHOI:
        return [f.branch, f.block];
      default:
        return [f.branch];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPosition, branch, area, block]);

  const sheetTitle =
    { AREA: "Vùng", BLOCK: "Khối", BRANCH: "Chi nhánh", SORT: "Sắp xếp" }[sheet.type] || "";

  const sheetData = useMemo(() => {
    if (sheet.type === "SORT")
      return SORT_OPTIONS.map((o) => ({ key: o.key, label: o.label, raw: o.key }));
    if (sheet.type === "AREA") return buildOptionSheetData(options.items, area);
    if (sheet.type === "BLOCK") return buildOptionSheetData(options.items, block);
    if (sheet.type === "BRANCH") return buildOptionSheetData(options.items, branch);
    return [];
  }, [sheet.type, options.items, area, block, branch]);

  const selectedKey = {
    SORT: sortKey,
    AREA: area?._id,
    BLOCK: block?._id,
    BRANCH: branch?._id,
  }[sheet.type];

  const onPick = (raw) => {
    if (sheet.type === "SORT") setSortKey(raw);
    if (sheet.type === "AREA") setArea(raw);
    if (sheet.type === "BLOCK") setBlock(raw);
    if (sheet.type === "BRANCH") setBranch(raw);
    sheet.close();
    setSheetSearch("");
  };

  const openDetail = (item) => {
    const q = new URLSearchParams({
      fullName: item?.fullName || "",
      code: item?.code || "",
    });
    navigate(
      `${EMP_BASE_PATH}/quan-ly/thu-nhap/cho-chi-tra/${item?.employeeId}?${q.toString()}`
    );
  };

  const statCards = [
    {
      subTitle: "NHÂN VIÊN",
      title: "NV còn khoản chưa nhận",
      value: summary?.totalEmployee ?? 0,
      icon: appImages.icon_khach_hang,
      bgColor: "#EDFDF5",
      textColor: "#2ABA82",
    },
    {
      subTitle: "GIAO DỊCH",
      title: "Giao dịch chưa trả hết",
      value: summary?.totalTransaction ?? 0,
      icon: appImages.icon_giao_dich,
      bgColor: "#EFF6FF",
      textColor: "#2F80ED",
    },
    {
      subTitle: "DỰ ÁN",
      title: "Số dự án còn tiền",
      value: summary?.totalProject ?? 0,
      icon: appImages.icon_du_an,
      bgColor: "#FFF7ED",
      textColor: "#F57127",
    },
    {
      subTitle: "ĐÃ CHI TRẢ",
      title: "PDV đã được chi trả",
      value: formatVnd(summary?.totalPaid || 0),
      icon: appImages.icon_giai_ngan,
      bgColor: "#F5F3FF",
      textColor: "#8B5CF6",
    },
  ];

  const selectSheet = (
    <SelectSheet
      isOpen={sheet.isOpen}
      onClose={() => {
        sheet.close();
        setSheetSearch("");
      }}
      title={sheetTitle}
      data={sheetData}
      selectedKey={selectedKey}
      onSelect={onPick}
      searchable={["AREA", "BLOCK", "BRANCH"].includes(sheet.type)}
      searchValue={sheetSearch}
      onChangeSearch={setSheetSearch}
      loading={options.loading}
      loadingMore={options.loadingMore}
      hasNextPage={options.hasMore}
      onEndReached={options.loadMore}
    />
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = items.map((it, i) => ({
      id: it?.employeeId || i,
      raw: it,
      name: employeeTitle(it) || "-",
      unpaid: formatVnd(it?.totalUnpaid || 0),
      transactions: String(it?.totalTransaction || 0),
      paid: formatVnd(it?.totalPaid || 0),
      afterTax: formatVnd(it?.totalRemainingRevenueAfterTax || 0),
      block: it?.saleBlock?.name || "-",
      area: it?.saleArea?.name || "-",
      branch: it?.branch?.name || "-",
    }));

    return (
      <DesktopPage title="Thu nhập chờ chi trả">
        <DesktopStatRow
          items={[
            {
              key: "summary",
              title: "TỔNG PDV CHƯA ĐƯỢC CHI TRẢ",
              total: formatVnd(summary?.totalUnpaid || 0),
              color: M.primary,
            },
            ...statCards.map((s) => ({
              key: s.subTitle,
              title: s.title,
              total: String(s.value ?? 0),
              color: s.textColor,
            })),
          ]}
        />

        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            {filterFields.map((el) => React.cloneElement(el, { width: "200px" }))}
            <Box w="220px">
              <Text fontSize="14px" fontWeight={500} color={M.baseContent}>
                Sắp xếp
              </Text>
              <Box h="6px" />
              <Box
                as="button"
                type="button"
                w="100%"
                h="38px"
                px="8px"
                textAlign="left"
                bg={M.field}
                borderRadius="8px"
                fontSize="14px"
                fontWeight={500}
                color={M.baseContent}
                onClick={() => sheet.open("SORT")}
              >
                {sortOption.label}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            emptyText="Không có nhân viên nào đang chờ chi trả"
            onRowClick={(row) => openDetail(row.raw)}
            rows={rows}
            headers={[
              { key: "name", label: "Nhân viên", align: "left", width: "18%" },
              {
                key: "unpaid",
                label: "Chưa được chi trả",
                align: "right",
                width: "14%",
                render: (row) => (
                  <Text fontSize="14px" fontWeight={600} color={M.primary}>
                    {row.unpaid}
                  </Text>
                ),
              },
              { key: "transactions", label: "Giao dịch chưa trả hết", width: "12%" },
              { key: "paid", label: "Đã được chi trả", align: "right", width: "13%" },
              { key: "afterTax", label: "Tổng PDV sau thuế", align: "right", width: "13%" },
              { key: "block", label: "Khối", align: "left", width: "10%" },
              { key: "area", label: "Vùng", align: "left", width: "10%" },
              { key: "branch", label: "Chi nhánh", align: "left", width: "10%" },
            ]}
          />

          <Box px="16px" pb="16px">
            <LoadMore
              hasNext={hasNext}
              loading={loading}
              onClick={() => fetchPage(page + 1, "append")}
            />
          </Box>
        </DesktopPanel>

        {selectSheet}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Thu nhập chờ chi trả" />

      <Box px="16px" pt="12px">
        <FilterPanel open={filterOpen} onToggle={() => setFilterOpen((p) => !p)}>
          {filterFields}
        </FilterPanel>

        <Box h="12px" />
        <SummaryCard
          text="TỔNG PDV CHƯA ĐƯỢC CHI TRẢ"
          total={formatVnd(summary?.totalUnpaid || 0)}
        />

        <Box h="12px" />
        <Flex flexWrap="wrap" justify="space-between" gap="12px">
          {statCards.map((s) => (
            <StatCard key={s.subTitle} {...s} />
          ))}
        </Flex>

        <ListHeaderRow
          title="Danh sách nhân viên"
          sortLabel={sortOption.label}
          onSort={() => sheet.open("SORT")}
        />

        {loading && !items.length ? (
          <CenterSpinner />
        ) : items.length === 0 ? (
          <EmptyText text="Không có nhân viên nào đang chờ chi trả" />
        ) : (
          <Flex direction="column" gap="10px">
            {items.map((it, i) => (
              <EmployeeCard
                key={it?.employeeId || i}
                item={it}
                onClick={() => openDetail(it)}
              />
            ))}
            <LoadMore
              hasNext={hasNext}
              loading={loading}
              onClick={() => fetchPage(page + 1, "append")}
            />
          </Flex>
        )}
      </Box>

      {selectSheet}
    </Box>
  );
};

export default IncomeUnpaidEmployeeList;
